import { onUnmounted } from 'vue';
import type { Ref } from 'vue';
import type { ClientMessage } from '@/types/generated';
import { WebSocketManager } from './webSocketSingleton';
import type { ConnectionStatus, ServerMessageMap } from './webSocketSingleton';

// Re-export types for backward compatibility
export type { ConnectionStatus };

interface UseWebSocketOptions {
  url?: string;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  maxReconnectDelay?: number;
  backoffFactor?: number;
}

interface UseWebSocketReturn {
  ws: Readonly<Ref<WebSocket | null>>;
  isConnected: Readonly<Ref<boolean>>;
  connectionStatus: Readonly<Ref<ConnectionStatus>>;
  lastError: Readonly<Ref<string | null>>;

  // Reconnection state
  reconnectAttempt: Readonly<Ref<number>>;
  maxReconnectAttempts: Readonly<Ref<number>>;
  manualReconnectAvailable: Readonly<Ref<boolean>>;

  // Methods
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: ClientMessage) => boolean;
  onMessage: <T extends keyof ServerMessageMap>(
    messageType: T,
    handler: (data: ServerMessageMap[T]) => void
  ) => () => void;
  manualReconnect: () => Promise<void>;
}

/**
 * WebSocket composable using singleton pattern
 *
 * This composable maintains the exact same API as before but uses a singleton
 * WebSocketManager internally to share connections across components.
 *
 * Key benefits:
 * - Single persistent connection across component transitions
 * - Automatic reference counting for connection lifecycle
 * - Multiple message handlers per message type
 * - Zero breaking changes to existing component code
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  // Get singleton instance
  const manager = WebSocketManager.getInstance();

  // Configure manager with provided options
  if (
    options.url ||
    options.reconnectAttempts ||
    options.reconnectDelay ||
    options.maxReconnectDelay ||
    options.backoffFactor
  ) {
    manager.configure(options);
  }

  // Acquire reference on composable creation
  manager.acquire();

  // Store registered handlers for cleanup
  const registeredHandlers: (() => void)[] = [];

  // Release reference on component unmount
  onUnmounted(() => {
    // Clean up all registered handlers
    registeredHandlers.forEach(unsubscribe => unsubscribe());
    registeredHandlers.length = 0;

    // Release singleton reference
    manager.release();
  });

  // Wrapper for onMessage that tracks handlers for cleanup
  const onMessage = <T extends keyof ServerMessageMap>(
    messageType: T,
    handler: (data: ServerMessageMap[T]) => void
  ): (() => void) => {
    const unsubscribe = manager.onMessage(messageType, handler);

    // Track handler for cleanup
    registeredHandlers.push(unsubscribe);

    return unsubscribe;
  };

  return {
    // Expose singleton state as readonly refs (exact same API)
    ws: manager.websocket,
    isConnected: manager.isConnected,
    connectionStatus: manager.status,
    lastError: manager.error,

    // Expose new reconnection state
    reconnectAttempt: manager.reconnectAttempt,
    maxReconnectAttempts: manager.maxReconnectAttempts,
    manualReconnectAvailable: manager.manualReconnectAvailable,

    // Expose singleton methods (exact same API)
    connect: () => manager.connect(),
    disconnect: () => manager.disconnect(),
    sendMessage: (message: ClientMessage) => manager.sendMessage(message),
    onMessage,
    manualReconnect: () => manager.manualReconnect(),
  };
}
